import React, { useState, useEffect, useCallback } from 'react';
import { analytics } from '../utils/analytics';

export const LandingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Track page view on mount
    analytics.pageView('/');
  }, []);

  const handleOrderClick = useCallback(async () => {
    analytics.buttonClick('order_button');
    analytics.orderInitiated();

    setIsLoading(true);
    try {
      // Dynamically import OAuth service to avoid circular dependencies
      const { initiateGmailOAuth } = await import('../services/oauth');
      initiateGmailOAuth();
      // Note: Page will redirect to Google, so setIsLoading(false) won't be reached
    } catch (error) {
      console.error('OAuth initiation failed:', error);
      analytics.error('OAuth failed', error instanceof Error ? error.stack : undefined);
      setIsLoading(false);
      alert('Failed to start Google sign-in. Please try again.');
    }
  }, []); // Empty deps: setIsLoading and analytics are stable

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <header className="text-center mb-16">
          <h1 className="text-6xl font-bold text-amber-900 mb-4">
            ☕ taptap.coffee
          </h1>
          <p className="text-xl text-amber-800 font-medium">
            Local coffee delivery, delivered fast
          </p>
        </header>

        {/* Hero Section */}
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl p-12">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Fresh Coffee, Delivered to Your Door
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Order from your favorite local coffee shops and get it delivered
              within minutes. No app downloads, no hassle.
            </p>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-semibold text-gray-800 mb-2">Fast Delivery</h3>
              <p className="text-sm text-gray-600">Get your order in 15-30 minutes</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <div className="text-4xl mb-3">🏪</div>
              <h3 className="font-semibold text-gray-800 mb-2">Local Shops</h3>
              <p className="text-sm text-gray-600">Support your neighborhood cafes</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <div className="text-4xl mb-3">📧</div>
              <h3 className="font-semibold text-gray-800 mb-2">Email Simple</h3>
              <p className="text-sm text-gray-600">Order via Gmail, no app needed</p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <button
              onClick={handleOrderClick}
              disabled={isLoading}
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold py-4 px-12 rounded-full text-xl shadow-lg transform transition hover:scale-105 active:scale-95 disabled:scale-100 disabled:hover:scale-100 flex items-center justify-center gap-3 mx-auto min-w-[280px]"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : (
                'Order Local Delivery'
              )}
            </button>
            <p className="mt-4 text-sm text-gray-500">
              Sign in with Gmail to get started
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center mt-16 text-gray-600">
          <p className="text-sm">
            Powered by Cloudflare Workers • Built for speed
          </p>
        </footer>
      </div>
    </div>
  );
};
